import { getActivePinia } from 'pinia';
import { useRoute } from 'vue-router';
import { useAccountStore } from '@core/stores/account.store';
import { STUDIO_CRUD } from '@core/models/permissions';
import { useDeployInfo } from './useDeployInfo';
import { useDebugRequestHistory, sanitizeData } from './useDebugRequestHistory';

/**
 * Static architecture documentation included in the export
 */
const ARCHITECTURE_DOC = {
  overview: 'Millers Events - Multi-tier Vue 3 + .NET e-commerce platform for photography studios',
  frontend: {
    stack: ['Vue 3 (Composition API)', 'Pinia (state management)', 'Tailwind CSS + DaisyUI', 'TypeScript', 'Vite'],
    entryPoint: 'src/main.ts → App.vue',
  },
  stores: {
    'account.store': {
      purpose: 'User authentication, JWT claims, permissions, user preferences',
      key: 'Contains hasPermission() for auth checks',
    },
    'features.store': {
      purpose: 'Feature flags per studio context',
      key: 'Loaded based on route studioId/studioSlug',
    },
    'adminLayout.store': {
      purpose: 'Layout state (sidebar collapsed, max-width, CSS classes)',
      key: 'UI layout management',
    },
    'cachedImages.store': {
      purpose: 'Client-side image caching',
      key: 'May contain base64 data (sanitized in export)',
    },
  },
  apps: {
    '/admin': 'System admin area (requires studio.crud permission)',
    '/studioadmin': 'Studio management UI',
    '/customer': 'Customer-facing shopping experience',
    '/cart': 'Shopping cart',
    '/checkout': 'Checkout flow',
    '/job-editor': 'Event/job configuration',
    '/package-management': 'Package builder',
  },
  apiPatterns: {
    authedAxios: 'Authenticated requests via JWT Bearer token',
    serviceResult: 'All API calls return ServiceResult<T> with { data } or { errors }',
    versioning: 'V2/V3 folders contain RSPO code; non-versioned may be shared with legacy',
  },
  keyComposables: {
    useAsync: 'useAsyncResult / useAsyncRequest for loading state management',
    useRouteIds: 'Type-safe route parameter extraction',
    useFeatureFlag: 'Feature flag checks in components',
    useDeployInfo: 'Branch/deployment info for dev environments',
  },
  componentNaming: {
    prefix: 'M (e.g., MButton, MModal, MTable)',
    global: 'Core components in src/core/components/',
    shared: 'Shared components in src/shared/components/M*/',
  },
};

export interface DebugExportData {
  exportedAt: string;
  exportVersion: string;
  environment: {
    userAgent: string;
    screenSize: { width: number; height: number };
    windowSize: { width: number; height: number };
    language: string;
    timezone: string;
    deployInfo: unknown;
  };
  route: {
    path: string;
    name: string | symbol | null | undefined;
    fullPath: string;
    params: Record<string, string | string[]>;
    query: Record<string, string | string[] | null>;
    hash: string;
  };
  user: {
    name: string;
    email: string;
    userId: string | undefined;
    studioId: number | null;
    permissions: string[];
    loggedIn: boolean;
  };
  stores: Record<string, unknown>;
  apiHistory: unknown[];
  architecture: typeof ARCHITECTURE_DOC;
}

/**
 * Checks if the current user has system admin permission
 */
export function hasDebugPermission(): boolean {
  try {
    const accountStore = useAccountStore();
    return accountStore.hasPermission(STUDIO_CRUD);
  } catch {
    // Store may not be initialized yet
    return false;
  }
}

/**
 * Collects all Pinia store state (sanitized)
 */
function collectStoreState(): Record<string, unknown> {
  const pinia = getActivePinia();
  if (!pinia) {
    return { _error: 'Pinia not initialized' };
  }

  const stores: Record<string, unknown> = {};

  // Access the internal store map
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const storeMap = (pinia as any)._s as Map<string, any>;

  if (storeMap && storeMap.forEach) {
    storeMap.forEach((store, id) => {
      try {
        // Get raw state from the store
        const state = store.$state;
        stores[id] = sanitizeData(state);
      } catch (e) {
        stores[id] = { _error: `Failed to serialize: ${e}` };
      }
    });
  }

  return stores;
}

/**
 * Triggers a file download with the given content
 */
function downloadJson(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Composable for exporting debug data
 */
export function useDebugExport() {
  const { deployInfo, load: loadDeployInfo } = useDeployInfo();
  const { getHistory, clearHistory, getRequestCount, getMaxSize, truncationThreshold, setTruncationThreshold } =
    useDebugRequestHistory();

  // Capture route and account store during setup phase
  const route = useRoute();
  const accountStore = useAccountStore();

  /**
   * Collects all debug data into a structured export
   */
  function collectDebugData(): DebugExportData {
    return {
      exportedAt: new Date().toISOString(),
      exportVersion: '1.0.0',
      environment: {
        userAgent: navigator.userAgent,
        screenSize: { width: screen.width, height: screen.height },
        windowSize: { width: window.innerWidth, height: window.innerHeight },
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        deployInfo: deployInfo.value,
      },
      route: {
        path: route.path,
        name: route.name,
        fullPath: route.fullPath,
        params: { ...route.params } as Record<string, string | string[]>,
        query: { ...route.query } as Record<string, string | string[] | null>,
        hash: route.hash,
      },
      user: {
        name: accountStore.name,
        email: accountStore.email,
        userId: accountStore.userId,
        studioId: accountStore.studioId,
        permissions: accountStore.permissions,
        loggedIn: accountStore.loggedIn,
      },
      stores: collectStoreState(),
      apiHistory: getHistory(),
      architecture: ARCHITECTURE_DOC,
    };
  }

  /**
   * Exports debug data to a JSON file download
   */
  async function exportDebugData(): Promise<void> {
    // Ensure deploy info is loaded
    await loadDeployInfo();

    const data = collectDebugData();
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `debug-export-${timestamp}.json`;

    downloadJson(data, filename);
  }

  /**
   * Estimates the size of the export in KB
   */
  function estimateExportSize(): number {
    try {
      const data = collectDebugData();
      const json = JSON.stringify(data);
      return Math.round(json.length / 1024);
    } catch {
      return 0;
    }
  }

  return {
    exportDebugData,
    collectDebugData,
    estimateExportSize,
    clearHistory,
    getRequestCount,
    getMaxSize,
    truncationThreshold,
    setTruncationThreshold,
    hasDebugPermission,
  };
}
